<template>
  <div class="trend-renderer">
    <template v-if="!isEmpty && chartData">
      <AnalyticsTrendRatio
        v-if="trendType === TREND_TYPE.RATIO"
        class="trend-renderer__chart trend-renderer__chart--ratio"
        :chart-data="chartData"
      />
      <AnalyticsTrendEvolution
        v-if="trendType === TREND_TYPE.EVOLUTION"
        class="trend-renderer__chart trend-renderer__chart--evolution"
        :chart-data="chartData"
        :style="{ width: `${chartData!.labels.length * 15}px` }"
      />
      <AnalyticsTrendVerticalFunnel
        v-if="trendType === TREND_TYPE.FUNNEL"
        class="trend-renderer__chart trend-renderer__chart--funnel"
        :chart-data="chartData"
      />
      <AnalyticsTrendDistribution
        v-if="trendType === TREND_TYPE.DISTRIBUTION"
        class="trend-renderer__chart trend-renderer__chart--distribution"
        :legend-position="legendPosition"
        :chart-data="chartData"
      />
    </template>
    <div
      v-else
      class="trend-renderer__empty"
    >
      <i class="empty__icon icon-error" />
      <p class="empty__text">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import type { LayoutPosition } from 'chart.js'
import { TREND_TYPE } from '~/constants/renderTypes'
import { type BaseDataSet, type TrendChartData } from '~/types/analytics'
import {
  getDefaultMeasureTranslation,
  getFunnelMeasureTranslation
} from '~/services/explore'
import { type Measure } from '~/types/cube'
import type { QueryResults } from '~/types/query'
import { getFormattedDimensionValue } from '~/services/analytics'
import type { ALL_MAIN_TIME_DIMENSION_TITLES } from '~/constants/timeDimension'
import { isPeriodInFuture } from '~/services/timeDimension'

const LARGE_DATA_TRIM_LIMIT = 9

export default {
  name: 'RendererTrend',
  props: {
    trendType: {
      type: String as PropType<TREND_TYPE>,
      default: TREND_TYPE.EVOLUTION
    },
    queryResults: {
      type: Object as PropType<QueryResults>,
      required: true
    },
    fillMissingData: {
      type: Boolean,
      default: false
    },
    legendPosition: {
      type: String as PropType<LayoutPosition>,
      default: 'right'
    }
  },
  setup() {
    return { TREND_TYPE }
  },
  computed: {
    errorMessage() {
      if (this.isEmpty) {
        return this.$t('dashboard.error.empty.trend')
      }
      return this.$t('dashboard.error.unknown')
    },
    // eslint-disable-next-line vue/return-in-computed-property
    chartData(): TrendChartData {
      switch (this.trendType) {
        case TREND_TYPE.RATIO: {
          return {
            labels: [''],
            datasets: this.getSeries(this.queryResults, 0, false)
          }
        }

        case TREND_TYPE.FUNNEL: {
          return {
            labels: [''],
            datasets: this.getSeries(this.queryResults)
          }
        }

        case TREND_TYPE.EVOLUTION: {
          // There is always one time dimension for evolution charts
          const timeDimension = this.queryResults.query.dimensions[0]!

          const timeDimensionTitle =
            timeDimension.shortTitle as (typeof ALL_MAIN_TIME_DIMENSION_TITLES)[number]

          return {
            labels: this.queryResults.data
              .map(_ => '')
              .slice(-LARGE_DATA_TRIM_LIMIT),
            isInFuture: this.queryResults.data
              .filter(resultSetItem => resultSetItem[timeDimension.name])
              .map(resultSetItem =>
                isPeriodInFuture(
                  resultSetItem[timeDimension.name] as string,
                  timeDimensionTitle
                )
              )
              .slice(-LARGE_DATA_TRIM_LIMIT),
            datasets: this.getSeries(this.queryResults, LARGE_DATA_TRIM_LIMIT)
          }
        }

        case TREND_TYPE.DISTRIBUTION: {
          // There is always one dimension for distribution charts
          const dimension = this.queryResults.query.dimensions[0]!

          return {
            labels: this.queryResults.data.map(c =>
              getFormattedDimensionValue(c[dimension.name], dimension)
            ),
            datasets: this.getSeries(this.queryResults)
          }
        }
      }
    },
    isEmpty() {
      if (
        [TREND_TYPE.DISTRIBUTION, TREND_TYPE.FUNNEL].includes(this.trendType)
      ) {
        const dataSum = this.chartData.datasets.reduce<number>(
          (dataSum, dataset) => {
            return dataset.data.reduce<number>(
              (sum, data) => sum + (data ?? 0),
              dataSum
            )
          },
          0
        )

        return dataSum === 0
      }
      return false
    }
  },
  methods: {
    getMeasureTranslation(measure: Measure) {
      if (this.trendType === TREND_TYPE.FUNNEL) {
        return getFunnelMeasureTranslation(measure)
      }
      return getDefaultMeasureTranslation(measure)
    },
    getSeries(
      queryResults: QueryResults,
      dataLengthLimit = 0,
      withLabel = true
    ): BaseDataSet[] {
      return queryResults.query.measures.map(measure => {
        const data = queryResults.data
          .map(resultSetItem => {
            const value = resultSetItem[measure.name]
            if (this.fillMissingData && value === 0) {
              return null
            }
            return value
          })
          .slice(-dataLengthLimit) as number[]

        return {
          key: measure.name,
          label: withLabel ? this.getMeasureTranslation(measure) : '',
          data,
          format: measure.meta.format
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.trend-renderer {
  height: 100%;
  display: flex;
  position: relative;

  &__chart {
    height: 80px;
    width: 160px;

    &--evolution {
      margin-top: 16px;
      height: 56px !important;
      width: 120px;
      position: relative;

      &::before {
        content: '';
        width: 132px;
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
      }
    }

    &--ratio {
      margin-top: -8px;
      height: 80px !important;
      width: 80px;
    }

    &--distribution,
    &--funnel {
      height: 100%;
      width: 100%;
      margin-top: 16px;
      position: absolute;
      left: 0;
    }
  }

  &__empty {
    height: 80px;
    width: 160px;
    margin: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .empty {
      &__icon {
        @include font-icon;
        @include font-size(24px);
        color: $text-secondary;
        margin-bottom: 4px;
      }

      &__text {
        @include font-text;
        @include font-size($font-size-mini);
        color: $text-secondary;
        text-align: center;
        margin-bottom: 24px;
      }
    }
  }
}
</style>
